import type { SavedItem } from '@/hooks/useSavedItems';
import type { SavedProduct } from '@/types/product';

/**
 * Focal point coordinates for image cropping
 */
export interface FocalPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Convert SavedItem to SavedProduct format for ProductCard display
 */
export function savedItemToProduct(item: SavedItem): SavedProduct {
  // Only use price if it exists and is greater than 0
  const hasReliablePrice = item.price != null && item.price > 0;
  
  // Calculate if extraction should still be shown (max 25 seconds from creation)
  const createdAt = new Date(item.created_at).getTime();
  const now = Date.now();
  const extractionTimeoutMs = 25 * 1000; // 25 seconds max
  const isWithinExtractionWindow = (now - createdAt) < extractionTimeoutMs;
  
  // Show extracting state only if:
  // 1. Not analyzed yet
  // 2. Missing core data (for URLs) or is image source
  // 3. Still within the extraction timeout window
  const isExtracting = !item.ai_analyzed && isWithinExtractionWindow && (
    (item.source_type === 'url' && !(item.image_url && item.name && item.price)) ||
    (item.source_type === 'image')
  );
  
  return {
    id: item.id,
    name: item.name || 'Saved Item',
    price: hasReliablePrice ? item.price : null,
    currency: item.currency || 'GBP',
    image: item.image_url || '',
    store: item.store || extractDomainFromUrl(item.url),
    originalPrice: item.original_price ?? undefined,
    priceDropPercent: item.price_drop_percent ?? undefined,
    inStock: item.in_stock ?? undefined,
    addedAt: item.created_at,
    url: item.url ?? undefined,
    isSavedItem: true,
    sourceType: item.source_type,
    focalPoint: item.ai_focal_point as FocalPoint | null ?? undefined,
    pinned_at: item.pinned_at ?? undefined,
    isExtracting,
    backInStockAt: item.back_in_stock_at ?? undefined,
    ai_category: item.ai_category ?? null,
  };
}

/**
 * Extract domain name from URL for display
 */
function extractDomainFromUrl(url: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '').split('/')[0];
  } catch {
    return url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] || '';
  }
}

/**
 * Social platforms for source grouping
 */
export const SOCIAL_PLATFORMS = ['instagram', 'tiktok', 'youtube', 'pinterest'] as const;
export type SocialPlatform = typeof SOCIAL_PLATFORMS[number];

/**
 * Detect social platform from URL
 */
export function detectPlatformFromUrl(url: string | null): SocialPlatform | null {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('instagram.com')) return 'instagram';
  if (lowerUrl.includes('tiktok.com')) return 'tiktok';
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) return 'pinterest';
  
  return null;
}

/**
 * Source types for item grouping
 */
export type ItemSourceType = 'social' | 'shopping' | 'uploads' | 'scanned';

/**
 * Determine the source type of an item
 */
export function getItemSourceType(item: SavedItem): ItemSourceType {
  if (item.source_type === 'image') return 'uploads';
  if (item.source_type === 'barcode') return 'scanned';
  
  const platform = item.platform?.toLowerCase() || detectPlatformFromUrl(item.url);
  if (platform && (SOCIAL_PLATFORMS as readonly string[]).includes(platform)) {
    return 'social';
  }
  
  return 'shopping';
}
